import { Component, OnInit } from '@angular/core';
import { Fitter } from '../../../fitter/models/fitter.model';
import { FitterService } from '../../../fitter/services/fitter.service';
import { WorkshopDefectFitter } from '../../models/workshop-defect-fitter.model';
import { WorkshopDefectFitterService } from '../../services/workshop-defect-fitter.service';
import { WorkshopDefect } from '../../models/workshop-defect.model';
import { WorkshopDefectService } from '../../services/workshop-defect.service';
import { WorkshopDefectValidatorService } from '../../validators/workshop-defect-validator.service';
import { VehicleSeverity } from '../../../workshop/models/vehicleseverity.model';
import { VehicleCheckService } from '../../../workshop/services/vehiclechecks.service';
import { FitterAbsenceService } from '../../../fitter/services/fitter-absence.service';
import { FitterAbsence } from '../../../fitter/models/fitter-absence.model';
import { take, catchError } from 'rxjs/operators';
import moment from 'moment';

@Component({
    selector: 'app-workshop-timeline',
    templateUrl: './workshop-timeline.component.html',
    styleUrls: ['./workshop-timeline.component.scss'],
    standalone: false
})
export class WorkshopTimelineComponent implements OnInit {

  fitters: Fitter[] = [];
  assignments: WorkshopDefectFitter[] = [];

  // Every open/in-progress defect, not just ones with zero assignments - a single defect can be
  // dragged onto several fitters/days (e.g. a multi-day job), so it has to stay available here
  // for as long as it's unfinished. It only drops off this list once signed off (status becomes
  // 'complete'), not the moment it gets its first assignment.
  activeDefects: WorkshopDefect[] = [];
  severities: VehicleSeverity[] = [];

  defectSearchTerm: string = '';
  defectSeverityFilter: number = -1;

  // Which fitters are on approved absence on the currently selected day - a drop onto their lane
  // is refused with a message. Deliberately not doing the same for a defect's vehicle being VOR:
  // a defect is very often the very reason its vehicle is off road, so blocking it from being
  // scheduled would stop it from ever getting fixed.
  absentFitterIds: Set<number> = new Set();

  date: moment.Moment = moment().startOf('day');

  hoveredFitterId: number = null;

  // The sidebar popout, opened from a card on either panel - editing happens here instead of
  // navigating away to the full edit page, so the timeline underneath is never lost.
  panelDefect: WorkshopDefect = null;
  isPanelOpen: boolean = false;
  panelIsError: boolean = false;
  panelIsServerError: boolean = false;

  private draggedDefectId: number = null;
  private draggedAssignmentId: number = null;

  constructor(
    private fitterService: FitterService,
    private workshopDefectFitterService: WorkshopDefectFitterService,
    private workshopDefectService: WorkshopDefectService,
    private workshopDefectValidator: WorkshopDefectValidatorService,
    private vehicleCheckService: VehicleCheckService,
    private fitterAbsenceService: FitterAbsenceService) { }

  ngOnInit(): void {
    this.fitterService.getAllFitters()
    .pipe(take(1))
    .subscribe((fitters: any) => {
      this.fitters = fitters;
    });

    this.vehicleCheckService.getAllVehicleSeverities()
    .pipe(take(1))
    .subscribe((severities: VehicleSeverity[]) => {
      this.severities = severities;
    });

    this.loadAssignments();
    this.loadActiveDefects();
    this.loadAvailability();
  }

  loadAvailability(): void {
    const dateStr = this.date.format('YYYY-MM-DD');

    this.fitterAbsenceService.getActiveAbsencesForDate(dateStr)
    .pipe(take(1))
    .pipe(catchError(() => []))
    .subscribe((absences: FitterAbsence[]) => {
      this.absentFitterIds = new Set((absences || []).map(a => a.fitterId));
    });
  }

  isFitterAbsent(fitterId: number): boolean {
    return this.absentFitterIds.has(fitterId);
  }

  filteredActiveDefects(): WorkshopDefect[] {
    const term = this.defectSearchTerm.trim().toLowerCase();

    return this.activeDefects.filter(d => {
      if(this.defectSeverityFilter !== -1 && d.severityId !== this.defectSeverityFilter) {
        return false;
      }

      if(term === '') {
        return true;
      }

      return d.vehicle.registration.toLowerCase().includes(term) || d.description.toLowerCase().includes(term);
    });
  }

  loadAssignments(): void {
    const dateStr = this.date.format('YYYY-MM-DD');

    this.workshopDefectFitterService.getBetweenDates(dateStr, dateStr)
    .pipe(take(1))
    .subscribe((assignments: WorkshopDefectFitter[]) => {
      this.assignments = assignments;
    });
  }

  loadActiveDefects(): void {
    this.workshopDefectService.getOpenDefects()
    .pipe(take(1))
    .subscribe((defects: WorkshopDefect[]) => {
      this.activeDefects = defects;
    });
  }

  signOff(defect: WorkshopDefect): void {
    defect.status = 'complete';
    this.workshopDefectService.updateWorkshopDefect(defect)
    .pipe(take(1))
    .subscribe(() => {
      this.loadActiveDefects();
      this.loadAssignments();
    });
  }

  prevDay(): void {
    this.date = this.date.clone().subtract(1, 'day');
    this.loadAssignments();
    this.loadAvailability();
  }

  nextDay(): void {
    this.date = this.date.clone().add(1, 'day');
    this.loadAssignments();
    this.loadAvailability();
  }

  today(): void {
    this.date = moment().startOf('day');
    this.loadAssignments();
    this.loadAvailability();
  }

  getFormattedDate(): string {
    return this.date.format('dddd MMMM Do YYYY');
  }

  assignmentsFor(fitterId: number): WorkshopDefectFitter[] {
    return this.assignments.filter(a => a.fitterId === fitterId);
  }

  statusLabel(status: string): string {
    return ({open: 'Open', assigned: 'Assigned', in_progress: 'In Progress', complete: 'Complete'} as any)[status] || status;
  }

  dragStartUnassigned(event: DragEvent, defect: WorkshopDefect): void {
    this.draggedDefectId = defect.id;
    this.draggedAssignmentId = null;
    event.dataTransfer.setData('text/plain', 'defect:' + defect.id);
  }

  dragStartAssignment(event: DragEvent, assignment: WorkshopDefectFitter): void {
    this.draggedAssignmentId = assignment.id;
    this.draggedDefectId = null;
    event.dataTransfer.setData('text/plain', 'assignment:' + assignment.id);
  }

  dragEnterLane(fitterId: number): void {
    this.hoveredFitterId = fitterId;
  }

  onDropOnLane(event: DragEvent, fitterId: number): void {
    event.preventDefault();
    this.hoveredFitterId = null;

    if(this.isFitterAbsent(fitterId)) {
      const fitter = this.fitters.find(f => f.id === fitterId);
      alert((fitter ? fitter.firstName + ' ' + fitter.lastName : 'This fitter') + ' is on leave today - work cannot be assigned to them.');
      this.draggedDefectId = null;
      this.draggedAssignmentId = null;
      return;
    }

    const dateStr = this.date.format('YYYY-MM-DD');

    if(this.draggedDefectId !== null) {
      this.workshopDefectFitterService.assignFitter(this.draggedDefectId, fitterId, dateStr)
      .pipe(take(1))
      .subscribe(() => {
        this.loadAssignments();
        this.loadActiveDefects();
      });
    } else if(this.draggedAssignmentId !== null) {
      const existing = this.assignments.find(a => a.id === this.draggedAssignmentId);
      this.workshopDefectFitterService.updateAssignment(this.draggedAssignmentId, existing.workshopDefectId, fitterId, dateStr, existing.hours)
      .pipe(take(1))
      .subscribe(() => {
        this.loadAssignments();
      });
    }

    this.draggedDefectId = null;
    this.draggedAssignmentId = null;
  }

  // Removes just this one fitter/day assignment - the defect itself, and any other days it's
  // scheduled on, are untouched. Distinct from signOff, which closes the whole defect.
  deleteAssignment(event: Event, assignment: WorkshopDefectFitter): void {
    event.preventDefault();
    event.stopPropagation();

    this.workshopDefectFitterService.removeAssignment(assignment.id)
    .pipe(take(1))
    .subscribe(() => {
      this.loadAssignments();
    });
  }

  // Quick way to extend a job onto the following day for the same fitter, without dragging it
  // in again from the Active Defects panel.
  copyToNextDay(event: Event, assignment: WorkshopDefectFitter): void {
    event.preventDefault();
    event.stopPropagation();

    const nextDay = this.date.clone().add(1, 'day').format('YYYY-MM-DD');
    this.workshopDefectFitterService.assignFitter(assignment.workshopDefectId, assignment.fitterId, nextDay)
    .pipe(take(1))
    .subscribe(() => {
      this.loadActiveDefects();
    });
  }

  openDefectPanel(event: Event, defectId: number): void {
    if(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.panelIsError = false;
    this.panelIsServerError = false;

    this.workshopDefectService.getWorkshopDefectById(defectId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if(e.status === 403 || e.status === 401){return e;}
      this.panelIsServerError = true;
      return e;
    }))
    .subscribe((defect: WorkshopDefect) => {
      this.panelDefect = defect;
      this.isPanelOpen = true;
    });
  }

  closePanel(): void {
    this.isPanelOpen = false;
    this.panelDefect = null;
  }

  savePanelDefect(): void {
    this.panelIsError = false;
    this.panelIsServerError = false;

    if(!this.workshopDefectValidator.isValid(this.panelDefect)) {
      this.panelIsError = true;
      return;
    }

    this.workshopDefectService.updateWorkshopDefect(this.panelDefect)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if(e.status === 403 || e.status === 401){return e;}
      this.panelIsServerError = true;
      return e;
    }))
    .subscribe(() => {
      this.loadAssignments();
      this.loadActiveDefects();
      this.closePanel();
    });
  }

}
